/**
 * Request bodies for the task write endpoints.
 *
 * These are pure and separately tested because this exact class of bug — a key
 * spelled almost right — has already cost real data, and the two failure modes
 * look nothing alike:
 *
 *   `completed_by`  instead of `completed_by_user_id`
 *       SILENT. The server destructures the long name, `|| null`s the missing
 *       value, and writes an explicit NULL. HTTP 200, `{"success": true}`, and
 *       `tasks.completed_by_user_id` is NULL on every task completed to date —
 *       so every "Completed by …" display has been blank, and the engagement
 *       events forwarded to Juggernaut carry a null actor.
 *
 *   `snooze_until`  instead of `snoozed_until`
 *       HARD 400, every time, before any DB access. Snooze has never worked.
 *       It went unnoticed because that 400 returns `message` (not `error`) and
 *       the client discarded both — see util/taskErrors.js.
 *
 * Field names verified against `taskController.js` on 2026-08-06.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/taskPayloads.test.mjs`. Do not add imports.
 */

/**
 * Body for `POST /api/tasks/:taskId/complete`.
 *
 * `completed_by_user_id` is a `users.User_id`. The server does not enforce it —
 * omitting it degrades to "nobody" rather than to an error — which is precisely
 * why it is built here rather than inline at three call sites.
 */
export function completeBody(userId, options = {}) {
  const body = {};
  if (userId !== null && userId !== undefined && userId !== '') {
    body.completed_by_user_id = userId;
  }
  const opts = options || {};
  if (opts.resolution_type) body.resolution_type = opts.resolution_type;
  if (opts.completion_note) body.completion_note = opts.completion_note;
  return body;
}

/**
 * Body for `POST /api/tasks/:taskId/snooze`.
 *
 * The column is `tasks.snoozed_until`; no `snooze_until` exists anywhere in the
 * server or schema. The value must parse in the future or the server 400s again.
 *
 * NOTE: callers pass a full `YYYY-MM-DD HH:mm:ss` wall-clock stamp, not a bare
 * date. That is deliberate — the snooze menu offers "In 1 hour" and "In 4 hours",
 * and a date-only value would collapse those to midnight today, which is not in
 * the future and would 400.
 */
export function snoozeBody(snoozedUntil) {
  return { snoozed_until: snoozedUntil };
}
